import React, { use, useState } from "react";

import { ProductCallToAction } from "@/redux/modules/api/data/cart/@types/cart.types";
import { CallToActionTypes } from "@/utils/enums";

const defaultContext: CallToActionsProviderValues = {
    modalState: false,
    closeDialog: () => {},
    openDialog: () => {},
    currentType: null,
    resetCurrentType: () => {},
    activeType: null,
    setActiveType: () => {},
    isLoading: false,
    setLoading: () => {},
    isExtendedActive: false,
    setExtendedActive: () => {},
    messages: [],
    addMessage: () => {}
};

export type CtaMessage = {
    key: string;
    message: string;
};

export type ActionType = {
    type: CallToActionTypes;
    sku?: string;
};

type CallToActionsProviderValues = {
    currentType: ActionType | null;
    resetCurrentType: (newtype: ActionType) => void;
    modalState: boolean;
    closeDialog: () => void;
    openDialog: () => void;
    activeType: ActionType | null;
    setActiveType: (newType: ActionType | null) => void;
    isLoading: boolean;
    setLoading: (isLoading: boolean) => void;
    isExtendedActive: boolean;
    setExtendedActive: (setExtendedActive: boolean) => void;
    messages: Array<CtaMessage>;
    addMessage: (key: string, message: string) => void;
};

const CallToActionsContext =
    React.createContext<CallToActionsProviderValues>(defaultContext);

type CallToActionsProviderProps = {
    children?: React.ReactNode;
    callToActionProducts?: Array<ProductCallToAction>;
};

export const CallToActionsProvider = ({
    children,
    callToActionProducts = []
}: CallToActionsProviderProps) => {
    const [currentType, setCurrentType] = useState(defaultContext.currentType);
    const [activeType, setActiveType] = useState(defaultContext.activeType);
    const [modalState, setModalState] = useState(defaultContext.modalState);
    const [isLoading, setLoading] = useState(defaultContext.isLoading);
    const [isExtendedActive, setExtendedActive] = useState(
        defaultContext.isExtendedActive
    );
    const [ctaMessages, setCtaMessages] = useState<any>({});

    const closeDialog = () => {
        setLoading(false);
        if (callToActionProducts.length > 0) {
            return;
        }

        setActiveType(defaultContext.activeType);
        setCtaMessages({});
        setExtendedActive(false);
        setCurrentType(defaultContext.currentType);

        const hasSilentActionCTAs = callToActionProducts.some((p) =>
            p.actions.some((a) => !a.visual)
        );

        if (hasSilentActionCTAs || callToActionProducts.length === 0) {
            setModalState(false);
        }
    };

    const openDialog = () => {
        setModalState(true);
    };

    const addMessage = (key: string, message: string) => {
        setCtaMessages({
            ...ctaMessages,
            [key]: message
        });
    };

    const resetCurrentType = (newValue: any) => {
        setActiveType(null);
        setCtaMessages({});
        setExtendedActive(false);
        setCurrentType(newValue ? { ...newValue } : null);
    };

    return (
        <CallToActionsContext
            value={{
                modalState,
                closeDialog,
                openDialog,
                currentType,
                resetCurrentType: resetCurrentType,
                activeType,
                setActiveType,
                isLoading,
                setLoading,
                isExtendedActive,
                setExtendedActive,
                messages: Object.keys(ctaMessages).map<CtaMessage>((k) => ({
                    key: k,
                    message: ctaMessages[k]
                })),
                addMessage
            }}
        >
            {children}
        </CallToActionsContext>
    );
};

export const useCallToActionsContext = (): CallToActionsProviderValues => {
    const context = use(CallToActionsContext);
    if (context === undefined) {
        throw new Error(
            "useCallToActionsProvider can only be used inside CallToActionsProvider"
        );
    }
    return context;
};
